/**
 * Year-range helpers shared by Work Experience + Residency/Fellowship
 * dialogs — PROD-761 commit 12 (extracted per @samer PR #128 review).
 *
 * Flutter renders both dialogs with identical year-bounds logic
 * (edit_work_experience_widget.dart dart:110-209 and
 * edit_residency_widget.dart dart:151-249). Two-dropdown shape:
 *
 *   - START YEAR: current year going back 100. Compute via
 *     `makeStartYearOptions(currentYear)`.
 *   - END YEAR: current year going back to the chosen start year (so
 *     end >= start always). If start is null, fall back to a 100-year
 *     range. Compute via `makeEndYearOptions(currentYear, startYear)`.
 *   - When start moves above the current end value, clear end. Compute
 *     via `clampEndYearOnStartChange(newStart, currentEnd)`.
 *
 * `currentYear` is passed in by the caller (every render — see the
 * year-drift rationale on @samer PR #124). Don't bake `new Date()`
 * into this util; the caller decides freshness semantics.
 *
 * Qualifications dialog uses only a single year dropdown (graduation
 * year, stored on `duration.end`) so it doesn't import this module.
 */
export function makeStartYearOptions(currentYear: number): number[] {
  return Array.from({ length: 101 }, (_, i) => currentYear - i);
}

export function makeEndYearOptions(
  currentYear: number,
  startYear: number | null,
): number[] {
  const endYearMin = startYear ?? currentYear - 100;
  return Array.from(
    { length: currentYear - endYearMin + 1 },
    (_, i) => currentYear - i,
  );
}

/**
 * Returns the end-year value to keep after the start year changes.
 * Replicates Flutter dart:178-182 / dart:218-222: if the new start is
 * above the current end, clear end (it would render as an out-of-range
 * value in the rebuilt dropdown). Otherwise preserve end as-is.
 *
 * Returns `null` to mean "clear", or the existing value to mean "keep".
 */
export function clampEndYearOnStartChange(
  newStart: number | null,
  currentEnd: number | null,
): number | null {
  if (newStart === null || currentEnd === null) return currentEnd;
  return currentEnd < newStart ? null : currentEnd;
}
